import axios from "axios";
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { QaAccess, QaState } from "./types";

const emptyState: QaState = { revision: 0, isPending: false, reviewedAt: null, reviewedBy: null, notes: [] };
const emptyAccess: QaAccess = { canAccess: false, canViewOrder: false, canEditNotes: false, isEligible: false };

interface QaContextValue {
  access: QaAccess;
  state: QaState;
  loading: boolean;
  error: boolean;
  saving: boolean;
  drafts: Record<number, string>;
  dirty: boolean;
  qaOnly: boolean;
  setQaOnly: (value: boolean) => void;
  setDraft: (taskId: number, text: string | undefined) => void;
  saveNote: (taskId: number) => Promise<void>;
  review: () => Promise<void>;
  reload: () => Promise<void>;
}

const ChecklistQaContext = createContext<QaContextValue | null>(null);
export const useChecklistQa = () => {
  const context = useContext(ChecklistQaContext);
  if (!context) throw new Error("Checklist QA provider is required");
  return context;
};

export function ChecklistQaProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const { taskType } = useParams();
  const enabled = taskType === "closingchecklist" || location.pathname.endsWith("/closingchecklist");
  const orderNumber = params.get("order");
  const { enqueueSnackbar } = useSnackbar();
  const [access, setAccess] = useState(emptyAccess);
  const [state, setState] = useState(emptyState);
  // Preserve drafts across row filtering without rerendering the checklist on every key.
  const drafts = useRef<Record<number, string>>({});
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const qaOnly = access.canViewOrder && params.get("qaOnly") === "true";
  const updateDirty = (notes = state.notes) => setDirty(Object.entries(drafts.current).some(([id, text]) =>
    text.trim() !== (notes.find((n) => n.taskId === Number(id))?.text || "")));

  const reload = async () => {
    if (!enabled || !orderNumber) return;
    setLoading(true);
    setError(false);
    try {
      const { data: permissions } = await axios.get<QaAccess>("/api/ClosingChecklistQa/Access", { params: { orderNumber } });
      setAccess(permissions);
      if (permissions.canViewOrder) {
        const { data } = await axios.get<QaState>("/api/ClosingChecklistQa/Order", { params: { orderNumber } });
        setState(data);
        updateDirty(data.notes);
      } else setState(emptyState);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setAccess(emptyAccess);
    setState(emptyState);
    drafts.current = {};
    setDirty(false);
    void reload();
  }, [orderNumber, enabled]);

  useEffect(() => {
    if (!dirty) return;
    const onUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [dirty]);

  const setDraft = (id: number, text: string | undefined) => {
    if (text === undefined) delete drafts.current[id];
    else drafts.current[id] = text;
    updateDirty();
  };

  const write = async (taskId?: number) => {
    if (busy.current || loading || error) return;
    busy.current = true;
    setSaving(true);
    try {
      await axios.post(`/api/ClosingChecklistQa/${taskId === undefined ? "Review" : "Note"}`, {
        orderNumber, revision: state.revision,
        ...(taskId === undefined ? {} : { taskId, text: drafts.current[taskId] })
      });
      if (taskId !== undefined) setDraft(taskId, undefined);
      enqueueSnackbar(taskId === undefined ? "QA marked as reviewed." : "QA note saved.", { variant: "success" });
      await reload();
    } catch (e) {
      const conflict = axios.isAxiosError(e) && e.response?.status === 409;
      enqueueSnackbar(conflict
        ? "QA has changed. Latest findings were loaded; check them before saving or reviewing again."
        : "Could not save QA. Your note is still here; please try again.", { variant: "error" });
      if (conflict) await reload();
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  return <ChecklistQaContext.Provider value={{
    access, state, drafts: drafts.current, loading, error, saving, dirty, qaOnly, setDraft,
    setQaOnly: (value) => setParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set("qaOnly", "true"); else next.delete("qaOnly");
      return next;
    }, { replace: true }),
    saveNote: (taskId) => write(taskId), review: () => write(), reload
  }}>{children}</ChecklistQaContext.Provider>;
}
